import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { JobFormData, EMPLOYMENT_TYPES, TIME_COMMITMENTS } from "../types/jobTypes";
import { useTranslation } from "react-i18next";

interface JobDetailsTabProps {
  jobData: JobFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, field: keyof JobFormData) => void;
  handleSkillsKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, field: keyof JobFormData) => void;
  handleEmploymentTypeChange: (type: string, checked: boolean) => void;
  handleTimeCommitmentChange: (value: string) => void;
  isEmploymentTypeSelected: (type: string) => boolean;
  showBudgetPerm: boolean;
  showBudgetFreelance: boolean;
}

export const JobDetailsTab = ({
  jobData,
  handleInputChange,
  handleSkillsKeyDown,
  handleEmploymentTypeChange,
  handleTimeCommitmentChange,
  isEmploymentTypeSelected,
  showBudgetPerm,
  showBudgetFreelance
}: JobDetailsTabProps) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            {t("jobs.formWizard.employmentType")}
          </Label>
          <div className="space-y-2">
            {EMPLOYMENT_TYPES.map((type) => (
              <div key={type} className="flex items-center">
                <Checkbox
                  id={`employment-type-${type}`}
                  checked={isEmploymentTypeSelected(type)}
                  onCheckedChange={(checked) => handleEmploymentTypeChange(type, !!checked)}
                  className="mr-2"
                />
                <Label htmlFor={`employment-type-${type}`} className="text-sm">
                  {t(`jobs.formWizard.employmentTypes.${type}`)}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            {t("jobs.formWizard.timeCommitment")}
          </Label>
          <div className="space-y-2">
            {TIME_COMMITMENTS.map((commitment) => (
              <div key={commitment} className="flex items-center">
                <Checkbox
                  id={`time-commitment-${commitment}`}
                  checked={jobData.timeCommitment === commitment}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      handleTimeCommitmentChange(commitment);
                    }
                  }}
                  className="mr-2"
                />
                <Label htmlFor={`time-commitment-${commitment}`} className="text-sm">
                  {t(`jobs.formWizard.timeCommitments.${commitment}`)}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {jobData.employmentType?.includes("Contract") && (
        <div>
          <Label htmlFor="contractDuration" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.contractDuration")}
          </Label>
          <Input
            id="contractDuration"
            type="text"
            value={jobData.contractDuration}
            onChange={(e) => handleInputChange(e, "contractDuration")}
            placeholder={t("jobs.formWizard.contractDurationPlaceholder")}
          />
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="startDate" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.startDate")}
          </Label>
          <Input
            id="startDate"
            type="date"
            value={jobData.startDate}
            onChange={(e) => handleInputChange(e, "startDate")}
          />
        </div>
        <div>
          <Label htmlFor="endDate" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.endDate")}
          </Label>
          <Input
            id="endDate"
            type="date"
            value={jobData.endDate}
            onChange={(e) => handleInputChange(e, "endDate")}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="seniorityLevel" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.seniorityLevel")}
          </Label>
          <Select
            value={jobData.seniorityLevel}
            onValueChange={(value) => {
              handleInputChange(
                { target: { value } } as React.ChangeEvent<HTMLSelectElement>,
                "seniorityLevel"
              )
            }}
          >
            <SelectTrigger id="seniorityLevel">
              <SelectValue placeholder={t("jobs.formWizard.selectSeniorityLevel")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="Entry Level">{t("jobs.formWizard.seniorityLevels.entryLevel")}</SelectItem>
                <SelectItem value="Junior">{t("jobs.formWizard.seniorityLevels.junior")}</SelectItem>
                <SelectItem value="Mid-Level">{t("jobs.formWizard.seniorityLevels.midLevel")}</SelectItem>
                <SelectItem value="Senior">{t("jobs.formWizard.seniorityLevels.senior")}</SelectItem>
                <SelectItem value="Lead">{t("jobs.formWizard.seniorityLevels.lead")}</SelectItem>
                <SelectItem value="Manager">{t("jobs.formWizard.seniorityLevels.manager")}</SelectItem>
                <SelectItem value="Director">{t("jobs.formWizard.seniorityLevels.director")}</SelectItem>
                <SelectItem value="Executive">{t("jobs.formWizard.seniorityLevels.executive")}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        {showBudgetPerm && (
          <div>
            <Label htmlFor="budget" className="text-sm font-medium text-gray-700 mb-1 block">
              {t("jobs.formWizard.budgetPerm")}
            </Label>
            <Input
              id="budget"
              type="text"
              value={jobData.budget}
              onChange={(e) => handleInputChange(e, "budget")}
              placeholder={t("jobs.formWizard.budgetPermPlaceholder")}
            />
          </div>
        )}
        
        {showBudgetFreelance && (
          <div>
            <Label htmlFor="budgetFreelance" className="text-sm font-medium text-gray-700 mb-1 block">
              {t("jobs.formWizard.budgetFreelance")}
            </Label>
            <Input
              id="budgetFreelance"
              type="text"
              value={jobData.budgetFreelance}
              onChange={(e) => handleInputChange(e, "budgetFreelance")}
              placeholder={t("jobs.formWizard.budgetFreelancePlaceholder")}
            />
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="locationType" className="text-sm font-medium text-gray-700 mb-1 block">
            {t("jobs.formWizard.locationType")}
          </Label>
          <Select
            value={jobData.locationType}
            onValueChange={(value) => {
              handleInputChange(
                { target: { value } } as React.ChangeEvent<HTMLSelectElement>,
                "locationType"
              )
            }}
          >
            <SelectTrigger id="locationType">
              <SelectValue placeholder={t("jobs.formWizard.selectLocationType")} />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="On-site">{t("jobs.formWizard.locationTypes.onsite")}</SelectItem>
                <SelectItem value="Remote">{t("jobs.formWizard.locationTypes.remote")}</SelectItem>
                <SelectItem value="Hybrid">{t("jobs.formWizard.locationTypes.hybrid")}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        {jobData.locationType === "Hybrid" && (
          <div>
            <Label htmlFor="workplaceBreakdown" className="text-sm font-medium text-gray-700 mb-1 block">
              {t("jobs.formWizard.workplaceBreakdown")}
            </Label>
            <Input
              id="workplaceBreakdown"
              type="text"
              value={jobData.workplaceBreakdown || ""}
              onChange={(e) => handleInputChange(e, "workplaceBreakdown")}
              placeholder={t("jobs.formWizard.workplaceBreakdownPlaceholder")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
